/**
 * Pure rules for "Prove Without Reveal" — a zero-knowledge compliance prover.
 *
 * The player is a prover holding a PRIVATE authority DAG (the witness). An
 * auditor issues a sequence of compliance challenges; the player constructs an
 * accepting transcript by selecting commit/response moves. The move generator
 * IS the theorem:
 *
 *   ZERO-KNOWLEDGE (ZKP-1): there is NO "reveal the witness / disclose an edge"
 *   move in the move set. Disclosure is structurally absent — never punished —
 *   so the auditor is convinced without ever seeing the DAG.
 *
 *   SOUNDNESS (ZKP-3): the legal-move generator produces an accepting response
 *   only when an ACTUAL DAG reachability path exists for the challenge. For a
 *   non-compliant challenge (no path), no accepting transcript is constructable
 *   — there is no "forge acceptance" move. The accepting response is absent
 *   because there is no witness, as opposed to a DENIED stamp on a forgery.
 *
 * This file is pure TypeScript: no React, no requestAnimationFrame. The panel
 * drives it with useState/useReducer.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import levelsData from "../data/proveWithoutRevealLevels.json" with { type: "json" };

export type ProverPhase = "select" | "committed" | "answered";
export type ProveWithoutRevealStatus = "playing" | "verified";

export interface ComplianceChallenge {
  readonly id: string;
  readonly theorem: string;
  readonly statement: string;
  readonly subject: string;
  readonly authority: string;
  /** Whether the prover's private DAG actually contains a reachability path. */
  readonly witnessHeld: boolean;
  readonly auditorNote: string;
}

export interface ProveWithoutRevealLevel {
  readonly id: string;
  readonly name: string;
  readonly brief: string;
  /** True when every challenge has a witness — the level is fully completable. */
  readonly compliant: boolean;
  readonly witnessSummary: string;
  readonly challenges: readonly ComplianceChallenge[];
}

/** One accepting commit→response pair, recorded without the witness. */
export interface TranscriptEntry {
  readonly challengeId: string;
  readonly theorem: string;
  readonly statement: string;
  readonly commitment: string;
  readonly response: string;
}

export interface ProveWithoutRevealState {
  readonly levelId: string;
  readonly levelName: string;
  readonly brief: string;
  readonly challenges: readonly ComplianceChallenge[];
  /** Index of the challenge the auditor is currently issuing. */
  readonly activeIndex: number;
  readonly phase: ProverPhase;
  readonly transcript: readonly TranscriptEntry[];
  /** Challenge ids the auditor confirmed have no constructable acceptance. */
  readonly stalledIds: readonly string[];
  readonly status: ProveWithoutRevealStatus;
  readonly lastEvent: string;
}

/**
 * The legal-move vocabulary. There is deliberately no `reveal-witness` and no
 * `forge-acceptance` constructor: those moves are structurally inexpressible,
 * not rejected at runtime.
 */
export type ProveWithoutRevealMove =
  | { readonly type: "select"; readonly index: number }
  | { readonly type: "commit" }
  | { readonly type: "respond" };

interface ProveWithoutRevealLevelsData {
  readonly levels: readonly ProveWithoutRevealLevel[];
}

export const PROVE_WITHOUT_REVEAL_LEVELS = (
  levelsData as ProveWithoutRevealLevelsData
).levels;

export const DEFAULT_PROVE_WITHOUT_REVEAL_LEVEL_ID =
  PROVE_WITHOUT_REVEAL_LEVELS[0]?.id ?? "";

const LEVEL_BY_ID = new Map(
  PROVE_WITHOUT_REVEAL_LEVELS.map((level) => [level.id, level]),
);

export function getProveWithoutRevealLevel(
  levelId: string,
): ProveWithoutRevealLevel {
  const level = LEVEL_BY_ID.get(levelId);
  if (!level) throw new Error(`Unknown Prove Without Reveal level: ${levelId}`);
  return level;
}

export function createProveWithoutRevealState(
  levelId = DEFAULT_PROVE_WITHOUT_REVEAL_LEVEL_ID,
): ProveWithoutRevealState {
  const level = getProveWithoutRevealLevel(levelId);
  return {
    levelId: level.id,
    levelName: level.name,
    brief: level.brief,
    challenges: level.challenges.map((challenge) => ({ ...challenge })),
    activeIndex: 0,
    phase: "select",
    transcript: [],
    stalledIds: [],
    status: "playing",
    lastEvent:
      "The auditor is ready. Open a challenge, then commit and respond — the witness never leaves your hands.",
  };
}

export function activeChallenge(
  state: ProveWithoutRevealState,
): ComplianceChallenge | null {
  return state.challenges[state.activeIndex] ?? null;
}

/**
 * The structural soundness gate (ZKP-3). An accepting response is constructable
 * for a challenge only when the prover's private DAG actually holds a
 * reachability path. No witness ⇒ no accepting move.
 */
export function acceptingResponseConstructable(
  challenge: ComplianceChallenge | null,
): boolean {
  return challenge?.witnessHeld === true;
}

/**
 * The legal-move generator. This is the theorem made operational. It NEVER
 * produces a `reveal-witness` move (zero-knowledge) and NEVER produces an
 * accepting `respond` move when the witness is absent (soundness).
 */
export function legalProveWithoutRevealMoves(
  state: ProveWithoutRevealState,
): readonly ProveWithoutRevealMove[] {
  if (state.status !== "playing") return [];
  const moves: ProveWithoutRevealMove[] = [];
  const challenge = activeChallenge(state);
  if (!challenge) return moves;

  if (state.phase === "select") {
    moves.push({ type: "select", index: state.activeIndex });
    return moves;
  }
  if (state.phase === "committed") {
    // Soundness: the accepting `respond` move exists only with a real witness.
    if (acceptingResponseConstructable(challenge)) {
      moves.push({ type: "respond" });
    }
    return moves;
  }
  return moves;
}

/**
 * ZERO-KNOWLEDGE guard (ZKP-1). The move set contains no constructor for
 * revealing the witness or disclosing a DAG edge. Asserted in tests.
 */
export function revealWitnessMoveGenerated(): boolean {
  return false;
}

/**
 * SOUNDNESS guard (ZKP-3). The move set contains no constructor for forging an
 * accepting transcript without a witness. Asserted in tests.
 */
export function forgeAcceptanceMoveGenerated(): boolean {
  return false;
}

function commitmentLabel(challenge: ComplianceChallenge): string {
  return `commit⟨${challenge.theorem}·${hashTag(challenge.id)}⟩`;
}

function responseLabel(challenge: ComplianceChallenge): string {
  return `accept⟨${challenge.subject}↠${challenge.authority}⟩`;
}

/** A stable, witness-free commitment tag derived from the challenge id only. */
function hashTag(id: string): string {
  let acc = 0;
  for (let i = 0; i < id.length; i += 1) {
    acc = (acc * 31 + id.charCodeAt(i)) % 0x10000;
  }
  return acc.toString(16).padStart(4, "0");
}

export function stepProveWithoutReveal(
  state: ProveWithoutRevealState,
  move: ProveWithoutRevealMove,
): ProveWithoutRevealState {
  if (state.status !== "playing") return state;
  const challenge = activeChallenge(state);
  if (!challenge) return state;

  if (move.type === "select") {
    if (state.phase !== "select") return state;
    return {
      ...state,
      lastEvent: `Auditor challenge: ${challenge.statement}`,
    };
  }

  if (move.type === "commit") {
    if (state.phase !== "select") return state;
    return {
      ...state,
      phase: "committed",
      lastEvent: `Committed ${commitmentLabel(challenge)} — no edge of the DAG is disclosed.`,
    };
  }

  // The only remaining move is "respond"; select and commit always returned above.
  if (state.phase !== "committed") return state;
  // Soundness: refuse to fabricate an accepting move with no witness. This is
  // not a runtime denial — the move was never in the legal set.
  if (!acceptingResponseConstructable(challenge)) {
    return {
      ...state,
      stalledIds: state.stalledIds.includes(challenge.id)
        ? state.stalledIds
        : [...state.stalledIds, challenge.id],
      lastEvent: `No reachability path from ${challenge.subject} to ${challenge.authority} exists in the DAG. By ZKP-3, an accepting transcript is unconstructable — there is no forge-acceptance move.`,
    };
  }
  const entry: TranscriptEntry = {
    challengeId: challenge.id,
    theorem: challenge.theorem,
    statement: challenge.statement,
    commitment: commitmentLabel(challenge),
    response: responseLabel(challenge),
  };
  return advanceAfterAccept(state, entry, challenge);
}

function advanceAfterAccept(
  state: ProveWithoutRevealState,
  entry: TranscriptEntry,
  challenge: ComplianceChallenge,
): ProveWithoutRevealState {
  const transcript = [...state.transcript, entry];
  const nextIndex = state.activeIndex + 1;
  const accepted = `${challenge.statement} — verified in zero knowledge.`;

  if (nextIndex >= state.challenges.length) {
    return {
      ...state,
      transcript,
      phase: "answered",
      status: "verified",
      lastEvent: `${accepted} Every challenge answered; the auditor is convinced, the witness never disclosed.`,
    };
  }
  return {
    ...state,
    transcript,
    activeIndex: nextIndex,
    phase: "select",
    lastEvent: `${accepted} Auditor issues the next challenge.`,
  };
}

export function transcriptAccepting(state: ProveWithoutRevealState): boolean {
  return (
    state.status === "verified" &&
    state.transcript.length === state.challenges.length
  );
}

export function answeredChallengeCount(
  state: ProveWithoutRevealState,
): number {
  return state.transcript.length;
}

export function remainingChallengeCount(
  state: ProveWithoutRevealState,
): number {
  return Math.max(0, state.challenges.length - state.transcript.length);
}
